import { Note02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import moment from "moment";
import { useMemo, useRef, useEffect } from "react";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import {
  DATE_FORMATS,
  mentionDescription
} from "shared/app/TaskTableView/constants";
import { ActivityLog, TaskActivity } from "shared/app/TaskTableView/types";
import { Hide } from "shared/components";

export const NoteItem = ({
  data,
  noteId
}: {
  data: ActivityLog;
  noteId?: number;
}) => {
  const noteItemRef = useRef<HTMLDivElement>(null);

  const currentNoteId = data.rich_text?.notifications.length
    ? data.rich_text.notifications[0].metadata.note_id
    : undefined;

  useEffect(() => {
    if (noteId && currentNoteId === noteId && noteItemRef.current)
      setTimeout(() => {
        noteItemRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }, 300);
  }, [noteId, currentNoteId]);
  const mentionEmailedUsers = useMemo(() => {
    if (data.activity_type !== TaskActivity.NOTE) return null;
    if (!data.rich_text?.notifications.length) return null;

    const userStrings = data.rich_text.notifications.map(
      (notification) => `${notification.user.name} (${notification.user.email})`
    );

    if (userStrings.length === 0) return null;
    if (userStrings.length === 1) return userStrings[0];
    if (userStrings.length === 2)
      return `${userStrings[0]} and ${userStrings[1]}`;
    return `${userStrings.slice(0, -1).join(", ")} and ${userStrings[userStrings.length - 1]}`;
  }, [data]);

  const isAllRead = useMemo(() => {
    if (!data.rich_text?.notifications.length) return true;

    return data.rich_text.notifications.every((n) => n.read_at);
  }, [data]);

  const isMailed = data.rich_text?.notifications.length
    ? data.rich_text.notifications[0].metadata.is_mailed
    : undefined;
  const noteDisplay =
    data.rich_text?.document_html || data.rich_text?.document_text || "";
  return (
    <div ref={noteItemRef} className="flex gap-2 min-h-[128px] px-4">
      <div className="relative w-10 flex mt-6 justify-center">
        <div className="grid size-10 place-items-center rounded-full border border-alpha-100 bg-background-1000">
          <HugeiconsIcon
            icon={Note02Icon}
            className="text-content-1000"
            size={20}
            stroke="1.5px"
          />
        </div>
        {!isAllRead && (
          <div className="absolute left-6 -top-0.5 size-3 bg-primary-500 border-2 border-background-900 rounded-full shrink-0" />
        )}
      </div>
      <div className="pt-6 max-w-[440px] w-full">
        <div className="space-y-3 bg-background-1000 border border-alpha-100 rounded-lg p-4">
          <div className="space-y-2">
            <p className="text-xs font-bold text-content-1000">
              {data.actor_name} <span className="font-normal">left a note</span>
            </p>
            <LiteTextEditor
              editable={false}
              initialValue={noteDisplay}
              id={`activity-note-${data.id}`}
              containerClassName="text-content-400 text-sm"
            />
          </div>
          <Hide if={!data.rich_text?.notifications.length}>
            <div className="h-px bg-alpha-40" />
            <p className="text-xs text-content-300">
              {mentionDescription(
                mentionEmailedUsers,
                isMailed,
                data.rich_text?.notifications.length
              )}
            </p>
          </Hide>
        </div>
        <div className="flex mt-2 px-4 justify-end">
          <time
            className="text-sm text-content-300 whitespace-nowrap text-ellipsis"
            dateTime={data.created_at}
          >
            {moment
              .utc(data.created_at)
              .format(`${DATE_FORMATS.DATE} [at] ${DATE_FORMATS.TIME}`)}
          </time>
        </div>
      </div>
    </div>
  );
};
